import {
    EPaymentType,
    ETransactionStatus,
    ETransactionStatusName,
    ETransactionType,
    ETransactionTypeName,
    PayrixPaymentMeta,
} from '../../@types/payrix';

/**
 * Detects whether a processorPaymentMeta object is in LawPay format.
 *
 * LawPay uses string values for type ("CHARGE"/"REFUND") and status ("AUTHORIZED"/"COMPLETED"/etc.),
 * while Payrix uses numeric enums for type (1-12) and status (0-5).
 *
 * Primary check: string type that is "CHARGE" or "REFUND".
 * Secondary check: presence of authorization_code (LawPay-specific) or absence of typeText (Payrix-specific).
 */
export function isLawpayFormat(meta: Record<string, any>): boolean {
    if (typeof meta.type === 'string' && (meta.type === 'CHARGE' || meta.type === 'REFUND')) return true;
    if ('authorization_code' in meta && !('authorization' in meta)) return true;
    return false;
}

/**
 * Maps a LawPay string type + payment method to the corresponding Payrix numeric type and text.
 */
function mapLawpayType(lpType: string, isBank: boolean): {type: ETransactionType, typeText: ETransactionTypeName} {
    if (lpType === 'REFUND') {
        return isBank
            ? {type: ETransactionType.ECheckRefund, typeText: ETransactionTypeName.ECheckRefund}
            : {type: ETransactionType.CreditCardRefund, typeText: ETransactionTypeName.CreditCardRefund};
    }
    return isBank
        ? {type: ETransactionType.ECheckSale, typeText: ETransactionTypeName.ECheckSale}
        : {type: ETransactionType.CreditCardSale, typeText: ETransactionTypeName.CreditCardSale};
}

/**
 * Maps a LawPay string status to the corresponding Payrix numeric status and text.
 *
 * Note: LawPay "VOIDED" has no direct Payrix numeric equivalent — Payrix detects voids
 * via batch === null, not a status code. We use Approved (1) as the numeric status with
 * "Voided" as the statusText, which matches how Payrix.parsePaymentMeta handles voids.
 */
function mapLawpayStatus(lpStatus: string): {status: ETransactionStatus, statusText: ETransactionStatusName} {
    switch (lpStatus) {
        case 'COMPLETED':
            return {status: ETransactionStatus.Settled, statusText: ETransactionStatusName.Settled};
        case 'VOIDED':
            return {status: ETransactionStatus.Approved, statusText: ETransactionStatusName.Voided};
        case 'PENDING':
            return {status: ETransactionStatus.Pending, statusText: ETransactionStatusName.Pending};
        case 'AUTHORIZED':
        default:
            return {status: ETransactionStatus.Approved, statusText: ETransactionStatusName.Approved};
    }
}

/**
 * Normalizes a processorPaymentMeta object from any format (LawPay or Payrix) into the
 * standard PayrixPaymentMeta format. Returns null if the input is empty or has no id.
 *
 * This handles legacy LawPay metadata that still exists in the database from before
 * the switch to Payrix. After normalization, all downstream code can safely assume
 * PayrixPaymentMeta without format branching.
 *
 * Field mapping (LawPay → Payrix):
 *   authorization_code → authorization
 *   type "CHARGE"/"REFUND" → numeric type + typeText
 *   status "AUTHORIZED"/etc. → numeric status + statusText
 *   method fields are structurally compatible (pass through)
 *
 * Note: For Payrix metadata that already has an id, this returns the object as-is.
 * It assumes any non-LawPay metadata with an id is well-formed Payrix data.
 */
export function normalizePaymentMeta(meta: any): PayrixPaymentMeta | null {
    if (!meta || typeof meta !== 'object' || !meta.id) return null;

    // Already in Payrix format — pass through as-is
    if (!isLawpayFormat(meta)) return meta as PayrixPaymentMeta;

    // Convert LawPay format → Payrix format
    const isBank = meta.method?.type === 'bank';
    const {type, typeText} = mapLawpayType(meta.type, isBank);
    const {status, statusText} = mapLawpayStatus(meta.status);

    // Only populate voidOrRefundDate when the transaction was actually voided or refunded.
    // Production LawPay data always has voidOrRefundDate populated (even for non-refunded txns),
    // which would cause misleading display in PaymentActionBox if passed through as-is.
    const isVoidedOrRefunded = meta.status === 'VOIDED' || (meta.amount_refunded != null && meta.amount_refunded > 0);
    const voidOrRefundDate = isVoidedOrRefunded && meta.voidOrRefundDate
        ? new Date(meta.voidOrRefundDate)
        : null;

    return {
        id: meta.id,
        type,
        typeText,
        status,
        statusText,
        authorization: meta.authorization_code ?? null,
        amount: meta.amount ?? 0,
        amount_refunded: meta.amount_refunded ?? 0,
        chargeDate: meta.chargeDate ? new Date(meta.chargeDate) : null,
        voidOrRefundDate,
        method: {
            type: isBank ? EPaymentType.Bank : EPaymentType.Card,
            number: meta.method?.number ?? '****',
            cardType: meta.method?.cardType ?? null,
            accountType: meta.method?.accountType ?? null,
            fingerprint: meta.method?.fingerprint ?? null,
        },
        // Fields that don't exist in LawPay — provide minimal defaults.
        // description uses a minimal valid JSON structure for downstream Zod validation.
        merchant: meta.merchant ?? '',
        description: meta.description ?? JSON.stringify({id: meta.id, isTrust: false, description: ''}),
        ipCreated: meta.ipCreated ?? '0.0.0.0',
        total: meta.total ?? (meta.amount ? Math.round(meta.amount * 100) : 0),
        settledTotal: meta.settledTotal ?? null,
        refunded: meta.refunded ?? null,
        approved: meta.approved ?? null,
        created: meta.created ?? null,
        modified: meta.modified ?? null,
    } as PayrixPaymentMeta;
}
